import { type UseMutationResult, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'

interface UseCrmRefreshResult {
  refresh: () => void
  isRefreshing: boolean
  error: Error | null
}

export function useCrmRefresh(
  twentyLeadId: string | undefined
): UseCrmRefreshResult {
  const { seenApi } = useSeenApi()

  const mutation: UseMutationResult<any, Error, void> = useMutation({
    mutationFn: async () => {
      if (!twentyLeadId) {
        throw new Error('Twenty Lead ID is required for CRM refresh')
      }

      const response = await seenApi.post('/crm/lead-to-participant', {
        twentyLeadId,
      })
      if (response.status !== 200) {
        throw new Error('Failed to sync CRM lead to participant')
      }

      return response.data
    },
    onSuccess: (data) => {
      const syncedItems = [
        ...(data.syncedFields || []),
        ...(data.syncedAttachments || []).map(() => 'attachment'),
        ...(data.syncedNotes || []).map(() => 'note'),
        ...(data.syncedCoverages || []),
      ]
      const syncedCount = syncedItems.length

      toast.success(
        syncedCount > 0
          ? `CRM sync completed successfully. Synced ${syncedCount} item${syncedCount !== 1 ? 's' : ''}.`
          : 'CRM sync completed. No changes detected.'
      )
    },
  })
  useErrorToast(mutation.error?.message)

  return {
    refresh: mutation.mutate,
    isRefreshing: mutation.isPending,
    error: mutation.error,
  }
}
